import React, { useState, useEffect, useMemo } from 'react';
import { 
  StarIcon
} from '@heroicons/react/24/solid';
import { 
  StarIcon as StarIconOutline,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';

const CarouselReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Filtrer et trier les meilleures reviews (note >= 4)
  const bestReviews = useMemo(() => {
    return reviews
      .filter(review => review.rating >= 4)
      .sort((a, b) => b.rating - a.rating || new Date(b.createdAt) - new Date(a.createdAt))
      .slice(0, 10); // Limiter à 10 meilleures reviews
  }, [reviews]);

  // Calculer la note moyenne
  const averageRating = useMemo(() => {
    if (reviews.length === 0) return 0;
    const sum = reviews.reduce((acc, review) => acc + review.rating, 0);
    return (sum / reviews.length).toFixed(1);
  }, [reviews]);

  useEffect(() => {
    setIsLoading(true);
    fetch('https://cvanalyzerback.osc-fr1.scalingo.io/api/reviews')
      .then((response) => response.json())
      .then((data) => {
        // Gérer les deux formats : ancien (array) et nouveau (object avec reviews)
        const reviewsData = Array.isArray(data) ? data : (data.reviews || []);
        setReviews(reviewsData);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Erreur lors de la récupération des avis :', error);
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    if (bestReviews.length > 0) {
      const interval = setInterval(() => {
        goToNext();
      }, 6000); // 6 secondes pour laisser le temps de lire

      return () => clearInterval(interval);
    }
  }, [bestReviews.length]);

  const goToNext = () => {
    if (!isTransitioning && bestReviews.length > 0) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % bestReviews.length);
        setIsTransitioning(false);
      }, 500);
    }
  };

  const goToPrevious = () => {
    if (!isTransitioning && bestReviews.length > 0) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentIndex((prevIndex) => (prevIndex - 1 + bestReviews.length) % bestReviews.length);
        setIsTransitioning(false);
      }, 500);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          star <= rating ? (
            <StarIcon key={star} className="w-5 h-5 text-yellow-500" />
          ) : (
            <StarIconOutline key={star} className="w-5 h-5 text-gray-300" />
          )
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
    <div className="card-surface p-4 md:p-8 max-w-4xl mx-auto">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (bestReviews.length === 0) {
    return null; // Ne rien afficher s'il n'y a pas de reviews
  }

  const currentReview = bestReviews[currentIndex];

  return (
    <div className="card-surface p-4 md:p-10 max-w-5xl mx-auto overflow-hidden">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-semibold text-gray-900">
          Avis utilisateurs
        </h2>
        
        {/* Note moyenne */}
        {reviews.length > 0 && (
          <div className="flex flex-wrap items-center justify-center gap-2 mb-2">
            <div className="flex items-center gap-2">
              {renderStars(Math.round(averageRating))}
              <span className="text-2xl font-bold text-gray-800">
                {averageRating}/5
              </span>
            </div>
            <span className="text-gray-600">
              basé sur {reviews.length} avis
            </span>
          </div>
        )}
      </div>

      {/* Carousel */}
      <div className="relative">
        <div className="bg-slate-50 rounded-xl p-4 md:p-10 min-h-[200px] border border-gray-100">
          <div
            className={`transition-all duration-500 ease-in-out ${
              isTransitioning ? 'opacity-0 transform translate-x-4' : 'opacity-100 transform translate-x-0'
            }`}
          >
            {/* Note de l'avis */}
            <div className="flex items-center justify-center mb-6">
              {renderStars(currentReview.rating)}
            </div>

            {/* Commentaire */}
            <blockquote className="text-center mb-6">
              <p className="text-base md:text-xl text-gray-700 italic leading-relaxed break-words">
                "{currentReview.comment !== 'Aucun commentaire' ? currentReview.comment : 'Excellent service !'}"
              </p>
            </blockquote>

            {/* Auteur */}
            <footer className="text-center">
              <div className="flex items-center justify-center gap-3">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-lg">
                  {currentReview.user?.username?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div className="text-left">
                  <p className="font-semibold text-gray-800">
                    {currentReview.user?.username || 'Utilisateur anonyme'}
                  </p>
                  <p className="text-sm text-gray-500">
                    {new Date(currentReview.createdAt).toLocaleDateString('fr-FR', {
                      month: 'long',
                      year: 'numeric'
                    })}
                  </p>
                </div>
              </div>
            </footer>
          </div>
        </div>

        {bestReviews.length > 1 && (
          <div className="flex items-center justify-center gap-4 mt-4">
            <button
              type="button"
              onClick={goToPrevious}
              className="p-2 rounded-full border border-gray-200 bg-white hover:bg-gray-50"
              aria-label="Avis précédent"
            >
              <ChevronLeftIcon className="w-5 h-5 text-gray-600" />
            </button>
            <div className="flex justify-center gap-2">
            {bestReviews.map((_, index) => (
              <button
                key={index}
                type="button"
                onClick={() => {
                  setIsTransitioning(true);
                  setTimeout(() => {
                    setCurrentIndex(index);
                    setIsTransitioning(false);
                  }, 300);
                }}
                className={`${
                  currentIndex === index
                    ? 'w-8 h-2 bg-blue-600 rounded-full'
                    : 'w-2 h-2 bg-gray-300 rounded-full'
                }`}
                aria-label={`Aller à l'avis ${index + 1}`}
              />
            ))}
            </div>
            <button
              type="button"
              onClick={goToNext}
              className="p-2 rounded-full border border-gray-200 bg-white hover:bg-gray-50"
              aria-label="Avis suivant"
            >
              <ChevronRightIcon className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CarouselReviews;
